import { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { BORDER_RADIUS, COLOURS, spacingMixin } from '@belong/themes';
import { IHeading, ISpacingProps, ShadowsKey } from '@belong/types';
import { Heading } from '../../styles/Typography/Heading/Heading';
import { UICard } from '../../styles/UICard';

export interface ICardV2 extends ISpacingProps {
  children?: ReactNode;
  className?: string;
  decoration?: ReactNode;
  hasBorder?: boolean /* border is required for selected state */;
  selected?: boolean;
  shadowVariant?: ShadowsKey;
}

const CardV2: FC<ICardV2> = ({ children, decoration, hasPadding, ...otherProps }) => (
  <CardBase data-testid="card-v2" {...otherProps} borderVariant="shadow">
    {decoration}
    <CardInner hasPadding={hasPadding}>{children}</CardInner>
  </CardBase>
);

export const CardBase = styled(UICard)<Pick<ICardV2, 'hasBorder' | 'selected'> & ISpacingProps>`
  ${spacingMixin}
  position: relative;

  ${({ hasBorder }) =>
    hasBorder &&
    css`
      border: 2px solid transparent;
    `}

  ${({ selected, hasBorder }): any =>
    selected &&
    hasBorder &&
    css`
      border: 2px solid ${COLOURS.BELONG_BLUE};
      background: ${COLOURS.LIGHT_BLUE};
    `}
`;

export const CardInner = styled.div<ISpacingProps>`
  ${({ hasMargin, hasPadding = { xs: 1.6, md: 2.4 } }) =>
    spacingMixin({
      hasMargin,
      hasPadding
    })}
`;

/**
 * Callers must omit attrs-backed props to use these defaults; explicitly passing `undefined` resets them.
 */
export const CardHeading = styled(Heading).attrs<IHeading>(props => ({
  alignment: props.alignment || 'left',
  variant: 'small'
}))`
  ${({ hasMargin, hasPadding = '1.6rem 1.6rem 0' }) =>
    spacingMixin({
      hasMargin,
      hasPadding
    })}

  border-top-left-radius: ${BORDER_RADIUS.CARD};
  border-top-right-radius: ${BORDER_RADIUS.CARD};
`;

CardV2.displayName = 'CardV2';
export default CardV2;
